import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

// Tooltip visibiblity time. We add the base time to a variable time based on the number of words.
const baseVisibleTime = 1750;
const wordIncrementVisibleTime = 250;

type Props = {
  arrow?: boolean;
  children: React.ReactNode;
  placement?: 'bottom' | 'left' | 'right' | 'top';
  text: string;
  trigger?: 'click' | 'hover';
};

export function Tooltip({
  text,
  placement = 'top',
  arrow = true,
  trigger = 'hover',
  children,
}: Props): React.ReactElement {
  const [isVisible, setIsVisible] = useState(false);

  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover') {
      setIsVisible(false);
    }
  };

  // Calculate visible time based on the number of words in the text.
  const visibleTime = baseVisibleTime + wordIncrementVisibleTime * text.trim().split(' ').length;

  const handleClick = () => {
    if (trigger === 'click') {
      setIsVisible(true);

      setTimeout(() => {
        setIsVisible(false);
      }, visibleTime);
    }
  };

  const placementClasses = {
    top: 'bottom-full left-1/2 mb-2 transform -translate-x-1/2',
    bottom: 'top-full left-1/2 mt-2 transform -translate-x-1/2',
    left: 'top-1/2 right-full mr-2 transform -translate-y-1/2',
    right: 'top-1/2 left-full ml-2 transform -translate-y-1/2',
  };

  const arrowPlacementClasses = {
    top: '-bottom-1 left-1/2 -translate-x-1/2',
    bottom: '-top-1 left-1/2 -translate-x-1/2',
    left: 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2',
    right: 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2',
  };

  const tooltipClasses = twMerge(
    'absolute z-10 p-1 px-2 bg-zinc-500 text-white text-xs font-aribau',
    placementClasses[placement],
    isVisible ? 'block' : 'hidden',
  );

  const arrowClasses = twMerge(
    'absolute w-2 h-2 rotate-45 bg-zinc-500 border-0',
    arrowPlacementClasses[placement],
  );

  if (!text) {
    // If there is no text, we return the children as is.
    return children as React.ReactElement;
  }

  return (
    <div
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div onClick={handleClick}>{children}</div>

      <div className={tooltipClasses}>
        {text}

        {arrow && <div className={arrowClasses}></div>}
      </div>
    </div>
  );
}
